import React, { useEffect, useState } from 'react';
import { http } from '@/lib/http';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { OfficeCopyManifest } from '@shared/office-recovery-copy';
type Case = {
    id: string;
    operator_id: number;
    operator_name?: string;
    received_at: string | null;
    revoked_at: string | null;
    expires_at: string;
    listed_files: number | null;
    received_files: number;
    code_attempts?: number;
};
type Details = Case & {
    manifest: OfficeCopyManifest | null;
    files_received: Record<string, {
        sha256: string;
        size: number;
    }>;
};
const base = '/api/admin/office-recovery-copies';
async function json<T>(path: string, body?: object): Promise<T> {
    const r = await http(base + path, body ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {});
    const value = await r.json();
    if (!r.ok)
        throw new Error(value.error || 'Could not load emergency recovery.');
    return value;
}
function saveBlob(blob: Blob, name: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
}
export default function OfficeEmergencyCopiesPage() {
    const [cases, setCases] = useState<Case[]>([]);
    const [email, setEmail] = useState('');
    const [operator, setOperator] = useState<{ id: number; name: string; email: string } | null>(null);
    const [reason, setReason] = useState('');
    const [confirmed, setConfirmed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [code, setCode] = useState('');
    const [expires, setExpires] = useState('');
    const [details, setDetails] = useState<Details | null>(null);
    const [error, setError] = useState('');
    async function refresh() { setCases((await json<{
        cases: Case[];
    }>('/')).cases); }
    useEffect(() => { void refresh().catch(e => setError(e.message)); }, []);
    async function action(fn: () => Promise<void>) { setBusy(true); setError(''); try {
        await fn();
    }
    catch (e) {
        setError(e instanceof Error ? e.message : 'Request failed.');
    }
    finally {
        setBusy(false);
    } }
    async function download(id: string) {
        const r = await http(`${base}/${details!.id}/files/${id}`);
        if (!r.ok)
            throw new Error('Could not download the verified file.');
        const name = r.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1] ?? `${id}.bin`;
        saveBlob(await r.blob(), name);
    }
    // Emergency copies span operators and cycles; do not restore Cockpit filters or navigation here.
    return <main className="max-w-4xl mx-auto space-y-5 p-6">
    <h1 className="text-2xl font-semibold">Emergency copies from phones</h1>
    <p>Use only when normal sending and account recovery have failed. Verify the operator and the phone before issuing a code. A copy preserves evidence for investigation; it does not complete jobs or authorise clearing the phone.</p>
    <section className="border border-amber-400 rounded p-4 space-y-3">
      <h2 className="font-semibold">Authorise one emergency copy</h2>
      <label className="block">Operator email<Input type="email" disabled={busy} value={email} onChange={e => { setEmail(e.target.value); setOperator(null); setCode(''); }}/></label>
      <Button variant="outline" disabled={busy || !email.trim()} onClick={() => void action(async () => {
        setOperator(null); setCode('');
        const result = await json<{ operator: { id: number; name: string; email: string } }>(`/operators?email=${encodeURIComponent(email.trim())}`);
        setOperator(result.operator);
      })}>Find operator</Button>
      {operator && <p role="status">Selected operator: <strong>{operator.name}</strong> — {operator.email}</p>}
      <label className="block">Why rescue is needed and how you verified the request<Textarea value={reason} onChange={e => setReason(e.target.value)} maxLength={1000}/></label>
      <label className="flex gap-2"><input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)}/>Normal sending and account recovery have failed; I have confirmed this request with the operator.</label>
      <Button disabled={busy || !confirmed || !operator || reason.trim().length < 10} onClick={() => void action(async () => {
            const result = await json<{
                code: string;
                expiresAt: string;
            }>('/', { operatorId: operator!.id, operatorEmail: operator!.email, reason, emergencyConfirmed: true });
            setCode(result.code);
            setExpires(result.expiresAt);
            await refresh();
        })}>Create emergency recovery code</Button>
      {code && <div className="space-y-2"><p>Give this six-digit code privately to the operator. They must enter it with their email address. It expires {new Date(expires).toLocaleString()}. The code lasts 15 minutes, locks after five incorrect attempts, and can start a copy on one phone. It cannot download anything. No email is sent automatically.</p><textarea readOnly value={code} aria-label="Recovery code to give to operator" className="border w-full p-2 font-mono"/><p>On the phone: Get Office Help → Emergency recovery → enter email and six-digit code → Send an emergency copy to the office.</p></div>}
    </section>
    {error && <p role="alert" className="text-red-700">{error}</p>}
    <Button variant="outline" disabled={busy} onClick={() => void action(refresh)}>Refresh copies</Button>
    <ul className="space-y-3">{cases.map(c => <li key={c.id} className="border rounded p-3 space-y-2">
      <p>{c.operator_name ?? `Operator ${c.operator_id}`} — {c.received_at ? 'Copy received; jobs not resolved' : c.revoked_at ? 'Code revoked' : (c.code_attempts ?? 0) >= 5 ? 'Code locked — issue a new code' : new Date(c.expires_at).getTime() < Date.now() ? 'Code expired' : 'Waiting / partly received'}</p>
      <p className="text-sm break-all">{c.id} · {c.received_files} files received / {c.listed_files ?? 'no list yet'}</p>
      <Button variant="outline" disabled={busy} onClick={() => void action(async () => { setDetails(await json<Details>(`/${c.id}`)); })}>Review copy</Button>
      {!c.revoked_at && <Button variant="outline" disabled={busy} onClick={() => void action(async () => { await json(`/${c.id}/revoke`, {}); setCode(''); await refresh(); })}>Revoke upload code</Button>}
    </li>)}</ul>
    {details && <section className="border rounded p-4 space-y-3"><h2 className="font-semibold">Review emergency copy</h2>
      <p className="break-all">{details.id}</p><p>Device-reported job details are unverified. Do not replay them automatically or treat an old manager signature as approval for a later visit.</p>
      {details.manifest ? <>
        {details.manifest.events.some(e => e.job.operatorId !== details.operator_id) && <p role="alert" className="text-amber-800">Some records have a different or unknown operator. Check ownership before using this evidence.</p>}
        <p>{details.manifest.events.filter(e => !e.contextOnly).length} waiting job records. Captured {new Date(details.manifest.capturedAt).toLocaleString()}.</p>
        <Button variant="outline" onClick={() => saveBlob(new Blob([JSON.stringify({ reference: details.id, manifest: details.manifest, filesReceived: details.files_received, jobsSynced: false, originalsMayBeRemoved: false }, null, 2)], { type: 'application/json' }), `emergency-copy-${details.id}.json`)}>Download job records and receipt</Button>
        <ul className="space-y-2">{details.manifest.files.map(f => <li key={f.id}>{f.kind === 'signature' ? 'Manager signature' : 'Photo'} · job record {f.eventId} · {f.state === 'missing' ? 'Unavailable on phone — investigate' : details.files_received[f.id] ? <Button variant="outline" disabled={busy} onClick={() => void action(() => download(f.id))}>Download verified copy</Button> : 'Not received yet'}</li>)}</ul>
      </> : <p>The phone has not sent its saved-work list yet.</p>}
    </section>}
  </main>;
}
